import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaPlus, FaCalculator, FaFileSignature, FaCheckCircle, FaInfoCircle, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../styles/pages/RRHH/FiniquitosPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Causales de término de contrato según Código del Trabajo de Chile
 */
const CAUSALES_TERMINO = [
    { value: 'ART_159_1', label: 'Art. 159 N°1 - Mutuo acuerdo de las partes' },
    { value: 'ART_159_2', label: 'Art. 159 N°2 - Renuncia del trabajador' },
    { value: 'ART_159_3', label: 'Art. 159 N°3 - Muerte del trabajador' },
    { value: 'ART_159_4', label: 'Art. 159 N°4 - Vencimiento del plazo convenido' },
    { value: 'ART_159_5', label: 'Art. 159 N°5 - Conclusión del trabajo o servicio' },
    { value: 'ART_159_6', label: 'Art. 159 N°6 - Caso fortuito o fuerza mayor' },
    { value: 'ART_160_1', label: 'Art. 160 N°1 - Falta de probidad, vías de hecho, injurias o conducta inmoral' },
    { value: 'ART_160_2', label: 'Art. 160 N°2 - Negociaciones prohibidas' },
    { value: 'ART_160_3', label: 'Art. 160 N°3 - No concurrencia sin causa justificada' },
    { value: 'ART_160_4', label: 'Art. 160 N°4 - Abandono del trabajo' },
    { value: 'ART_160_5', label: 'Art. 160 N°5 - Actos, omisiones o imprudencias temerarias' },
    { value: 'ART_160_6', label: 'Art. 160 N°6 - Perjuicio material causado intencionalmente' },
    { value: 'ART_160_7', label: 'Art. 160 N°7 - Incumplimiento grave de las obligaciones' },
    { value: 'ART_161_1', label: 'Art. 161 inc. 1° - Necesidades de la empresa' },
    { value: 'ART_161_2', label: 'Art. 161 inc. 2° - Desahucio del empleador' }
];

/**
 * Página de Finiquitos
 * Gestión de liquidaciones finales por término de contrato
 */
const FiniquitosPage = ({ token, id_company }) => {
    const [finiquitos, setFiniquitos] = useState([]);
    const [colaboradoresDisponibles, setColaboradoresDisponibles] = useState([]); // Sin finiquito pagado (para formularios)
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showCalcModal, setShowCalcModal] = useState(false);

    // Filtros
    const [filtroEstado, setFiltroEstado] = useState('PENDIENTE');

    // Formulario manual
    const [formData, setFormData] = useState({
        id_colaborador: '',
        fecha_aviso: '',
        fecha_fin_contrato: '',
        fecha_salida: '',
        causal_termino: '',
        dias_vacaciones_pendientes: 0,
        monto_vacaciones: 0,
        monto_indemnizacion: 0,
        monto_aviso_previo: 0,
        otros_montos: 0,
        observaciones: ''
    });

    // Formulario cálculo automático mejorado
    const [calcData, setCalcData] = useState({
        id_colaborador: '',
        fecha_aviso: '',
        fecha_fin_contrato: '',
        fecha_pago: '',
        causal_termino: '',
        dias_feriado_tomados: 0,
        incluir_gratificacion: true
    });

    const fetchColaboradores = useCallback(async () => {
        try {
            // Cargar colaboradores disponibles (sin finiquito pagado) para formularios
            const responseDisponibles = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company, excluir_finiquitados: 'true' },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (responseDisponibles.data.success) {
                setColaboradoresDisponibles(responseDisponibles.data.data);
            }
        } catch (error) {
            console.error('Error al cargar colaboradores:', error);
        }
    }, [id_company, token]);

    const fetchFiniquitos = useCallback(async () => {
        try {
            setLoading(true);
            const params = { id_company, estado: filtroEstado };

            const response = await axios.get(`${API_BASE_URL}/api/rrhh/finiquitos`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setFiniquitos(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar finiquitos:', error);
            Swal.fire('Error', 'No se pudieron cargar los finiquitos', 'error');
        } finally {
            setLoading(false);
        }
    }, [id_company, filtroEstado, token]);

    useEffect(() => {
        fetchColaboradores();
    }, [fetchColaboradores]);

    useEffect(() => {
        fetchFiniquitos();
    }, [fetchFiniquitos]);

    const handleCalcular = async (e) => {
        e.preventDefault();

        try {
            // Usar el nuevo endpoint de cálculo completo con motor legal
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/finiquitos/calcular-completo`,
                {
                    id_colaborador: calcData.id_colaborador,
                    fecha_aviso: calcData.fecha_aviso,
                    fecha_termino: calcData.fecha_fin_contrato,
                    causal_termino: calcData.causal_termino,
                    dias_feriado_tomados: calcData.dias_feriado_tomados || 0,
                    incluir_gratificacion: calcData.incluir_gratificacion
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                const calc = response.data.data;
                // Determinar si tiene derecho a indemnización
                const tieneIndemnizacion = calc.detalleIndemnizacion?.tieneDerechoIndemnizacion;
                const tope90UFAplicado = calc.detalleIndemnizacion?.tope90UFAplicado;

                Swal.fire({
                    title: 'Cálculo Legal de Finiquito',
                    html: `
                        <div style="text-align: left; font-size: 14px;">
                            <h4 style="margin-bottom: 10px; color: #1976D2;">Resumen del Cálculo</h4>

                            <div style="background: #f5f5f5; padding: 10px; border-radius: 5px; margin-bottom: 10px;">
                                <p><strong>Causal:</strong> ${calc.datosColaborador?.nombreCausal || calcData.causal_termino}</p>
                                <p><strong>Años de servicio:</strong> ${calc.detalleFeriado?.aniosTrabajados?.toFixed(2) || 0}</p>
                            </div>

                            <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px;">
                                <tr style="border-bottom: 1px solid #ddd;">
                                    <td style="padding: 5px;">Feriado proporcional (${calc.detalleFeriado?.diasFeriadoPendientes?.toFixed(1) || 0} días)</td>
                                    <td style="text-align: right; padding: 5px;">$${parseInt(calc.resumen?.montoFeriadoProporcional || 0).toLocaleString('es-CL')}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #ddd;">
                                    <td style="padding: 5px;">Sueldo proporcional</td>
                                    <td style="text-align: right; padding: 5px;">$${parseInt(calc.resumen?.montoSueldoProporcional || 0).toLocaleString('es-CL')}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #ddd;">
                                    <td style="padding: 5px;">Gratificación proporcional</td>
                                    <td style="text-align: right; padding: 5px;">$${parseInt(calc.resumen?.montoGratificacionProporcional || 0).toLocaleString('es-CL')}</td>
                                </tr>
                                ${tieneIndemnizacion ? `
                                <tr style="border-bottom: 1px solid #ddd;">
                                    <td style="padding: 5px;">Indemnización (${calc.detalleIndemnizacion?.aniosIndemnizables || 0} años)${tope90UFAplicado ? ' <span style="color: orange;">(Tope 90 UF)</span>' : ''}</td>
                                    <td style="text-align: right; padding: 5px;">$${parseInt(calc.resumen?.montoIndemnizacion || 0).toLocaleString('es-CL')}</td>
                                </tr>` : `
                                <tr style="border-bottom: 1px solid #ddd; color: #999;">
                                    <td style="padding: 5px;">Indemnización</td>
                                    <td style="text-align: right; padding: 5px;">No aplica</td>
                                </tr>`}
                                ${calc.detalleMesAviso?.aplicaIndemnizacionAviso ? `
                                <tr style="border-bottom: 1px solid #ddd;">
                                    <td style="padding: 5px;">Mes de aviso (${calc.detalleMesAviso?.diasAvisoDados || 0} días dados)</td>
                                    <td style="text-align: right; padding: 5px;">$${parseInt(calc.resumen?.montoMesAviso || 0).toLocaleString('es-CL')}</td>
                                </tr>` : ''}
                            </table>

                            <div style="background: #e8f5e9; padding: 10px; border-radius: 5px; text-align: center;">
                                <strong style="font-size: 16px;">Total Finiquito: $${parseInt(calc.resumen?.montoTotalFiniquito || 0).toLocaleString('es-CL')}</strong>
                            </div>

                            ${calc.indicadoresUsados?.uf ? `
                            <p style="font-size: 11px; color: #666; margin-top: 10px;">
                                UF utilizada: $${calc.indicadoresUsados.uf.toLocaleString('es-CL')} | Tope 90 UF: $${parseInt(calc.indicadoresUsados.uf * 90).toLocaleString('es-CL')}
                            </p>` : ''}
                        </div>
                    `,
                    icon: 'info',
                    width: 550,
                    showCancelButton: true,
                    confirmButtonText: 'Crear Finiquito',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#1976D2'
                }).then((result) => {
                    if (result.isConfirmed) {
                        createFiniquitoConCalculo(calc);
                    }
                });
            }
        } catch (error) {
            console.error('Error al calcular finiquito:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al calcular', 'error');
        }
    };

    // Crear finiquito usando el nuevo endpoint con cálculo legal
    const createFiniquitoConCalculo = async (calculo) => {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/finiquitos/crear-con-calculo`,
                {
                    id_company,
                    id_colaborador: calcData.id_colaborador,
                    fecha_aviso: calcData.fecha_aviso,
                    fecha_termino: calcData.fecha_fin_contrato,
                    causal_termino: calcData.causal_termino,
                    dias_feriado_tomados: calcData.dias_feriado_tomados || 0,
                    incluir_gratificacion: calcData.incluir_gratificacion
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                const pdfUrl = response.data.pdf_url;

                Swal.fire({
                    title: 'Finiquito Creado',
                    html: `
                        <p>El finiquito se ha creado con cálculo legal.</p>
                        <p>Total: <strong>$${parseInt(response.data.data.monto_total_finiquito || 0).toLocaleString('es-CL')}</strong></p>
                        ${pdfUrl ? '<p>El PDF está listo para descargar.</p>' : ''}
                    `,
                    icon: 'success',
                    showCancelButton: pdfUrl,
                    confirmButtonText: pdfUrl ? 'Descargar PDF' : 'Cerrar',
                    cancelButtonText: 'Cerrar'
                }).then((result) => {
                    if (result.isConfirmed && pdfUrl) {
                        window.open(pdfUrl.startsWith('http') ? pdfUrl : `${API_BASE_URL}/uploads/${pdfUrl}`, '_blank');
                    }
                });

                setShowCalcModal(false);
                resetCalcForm();
                fetchFiniquitos();
            }
        } catch (error) {
            console.error('Error al crear finiquito:', error);
            Swal.fire('Error', error.response?.data?.message || 'No se pudo crear el finiquito', 'error');
        }
    };

    const handleSubmitManual = async (e) => {
        e.preventDefault();

        try {
            // Mapear datos del formulario al formato del backend
            const finiquitoData = {
                id_company,
                id_colaborador: formData.id_colaborador,
                fecha_finiquito: formData.fecha_salida,
                fecha_ultimo_dia_trabajado: formData.fecha_fin_contrato,
                fecha_aviso: formData.fecha_aviso,
                causal_termino: formData.causal_termino,
                dias_vacaciones_pendientes: formData.dias_vacaciones_pendientes,
                monto_vacaciones: formData.monto_vacaciones,
                monto_indemnizacion: formData.monto_indemnizacion,
                otros_pagos: formData.otros_montos,
                detalle_causal: formData.observaciones
            };

            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/finiquitos`,
                finiquitoData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                const pdfUrl = response.data.pdf_url;

                if (pdfUrl) {
                    // Mostrar mensaje de éxito con opción de descargar PDF
                    Swal.fire({
                        title: 'Finiquito Creado',
                        html: `
                            <p>El finiquito se ha creado exitosamente.</p>
                            <p>El PDF ha sido generado y está listo para descargar.</p>
                        `,
                        icon: 'success',
                        showCancelButton: true,
                        confirmButtonText: 'Descargar PDF',
                        cancelButtonText: 'Cerrar'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            // Descargar el PDF
                            window.open(pdfUrl.startsWith('http') ? pdfUrl : `${API_BASE_URL}/uploads/${pdfUrl}`, '_blank');
                        }
                    });
                } else {
                    Swal.fire('Éxito', 'Finiquito creado correctamente', 'success');
                }

                setShowModal(false);
                resetForm();
                fetchFiniquitos();
            }
        } catch (error) {
            console.error('Error al crear finiquito:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
        }
    };

    const handleCambiarEstado = async (id, nuevoEstado) => {
        try {
            const result = await Swal.fire({
                title: '¿Estás seguro?',
                text: `Se cambiará el estado a ${nuevoEstado}`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, cambiar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                const response = await axios.patch(
                    `${API_BASE_URL}/api/rrhh/finiquitos/${id}/estado`,
                    { estado: nuevoEstado },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    Swal.fire('Éxito', 'Estado actualizado', 'success');
                    fetchFiniquitos();
                }
            }
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            Swal.fire('Error', 'No se pudo cambiar el estado', 'error');
        }
    };

    const handleEliminar = async (id) => {
        const result = await Swal.fire({
            title: '¿Eliminar finiquito?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d32f2f'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(
                    `${API_BASE_URL}/api/rrhh/finiquitos/${id}`,
                    {
                        params: { id_company },
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                if (response.data.success) {
                    Swal.fire('Eliminado', 'Finiquito eliminado exitosamente', 'success');
                    fetchFiniquitos();
                }
            } catch (error) {
                console.error('Error al eliminar finiquito:', error);
                Swal.fire('Error', error.response?.data?.message || 'No se pudo eliminar el finiquito', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            id_colaborador: '',
            fecha_aviso: '',
            fecha_fin_contrato: '',
            fecha_salida: '',
            causal_termino: '',
            dias_vacaciones_pendientes: 0,
            monto_vacaciones: 0,
            monto_indemnizacion: 0,
            monto_aviso_previo: 0,
            otros_montos: 0,
            observaciones: ''
        });
    };

    const resetCalcForm = () => {
        setCalcData({
            id_colaborador: '',
            fecha_aviso: '',
            fecha_fin_contrato: '',
            fecha_pago: '',
            causal_termino: '',
            dias_feriado_tomados: 0,
            incluir_gratificacion: true
        });
    };

    return (
        <div className="finiquitos-container">
            <div className="finiquitos-header">
                <h1><FaFileSignature /> Finiquitos</h1>
                <div className="header-buttons">
                    <button className="btn-calcular" onClick={() => { resetCalcForm(); setShowCalcModal(true); }}>
                        <FaCalculator /> Calcular Automático
                    </button>
                    <button className="btn-nuevo" onClick={() => { resetForm(); setShowModal(true); }}>
                        <FaPlus /> Finiquito Manual
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="finiquitos-filters">
                <div className="filter-group">
                    <label>Estado</label>
                    <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                        <option value="">Todos</option>
                        <option value="PENDIENTE">Pendientes</option>
                        <option value="PAGADO">Pagados</option>
                        <option value="ANULADO">Anulados</option>
                    </select>
                </div>
            </div>

            {/* Tabla de finiquitos */}
            {loading ? (
                <div className="loading">Cargando finiquitos...</div>
            ) : (
                <div className="finiquitos-table-container">
                    <table className="finiquitos-table">
                        <thead>
                            <tr>
                                <th>Colaborador</th>
                                <th>Fecha Aviso</th>
                                <th>Fecha Fin Contrato</th>
                                <th>Fecha Pago</th>
                                <th>Causal Término</th>
                                <th>Vacaciones</th>
                                <th>Indemnización</th>
                                <th>Total</th>
                                <th>Estado</th>
                                <th>PDF</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {finiquitos.length === 0 ? (
                                <tr>
                                    <td colSpan="11" style={{ textAlign: 'center', padding: '30px' }}>
                                        No se encontraron finiquitos
                                    </td>
                                </tr>
                            ) : (
                                finiquitos.map((finiq) => {
                                    const causal = CAUSALES_TERMINO.find(c => c.value === finiq.causal_termino);
                                    return (
                                        <tr key={finiq.id}>
                                            <td>{finiq.nombres} {finiq.apellido_paterno}</td>
                                            <td>{finiq.fecha_aviso ? new Date(finiq.fecha_aviso).toLocaleDateString('es-CL') : '-'}</td>
                                            <td>{finiq.fecha_ultimo_dia_trabajado ? new Date(finiq.fecha_ultimo_dia_trabajado).toLocaleDateString('es-CL') : '-'}</td>
                                            <td>{finiq.fecha_finiquito ? new Date(finiq.fecha_finiquito).toLocaleDateString('es-CL') : '-'}</td>
                                            <td className="causal-cell" title={causal?.label}>
                                                {causal?.label || finiq.causal_termino || '-'}
                                            </td>
                                            <td className="amount">${parseInt(finiq.monto_vacaciones || 0).toLocaleString('es-CL')}</td>
                                            <td className="amount">${parseInt(finiq.monto_indemnizacion || 0).toLocaleString('es-CL')}</td>
                                            <td className="amount total">${parseInt(finiq.monto_total_finiquito || 0).toLocaleString('es-CL')}</td>
                                            <td>
                                                <span className={`badge badge-${finiq.estado?.toLowerCase() || 'pendiente'}`}>
                                                    {finiq.estado || 'PENDIENTE'}
                                                </span>
                                            </td>
                                            <td className="pdf-cell">
                                                {finiq.pdf_url ? (
                                                    <a
                                                        href={finiq.pdf_url.startsWith('http') ? finiq.pdf_url : `${API_BASE_URL}/uploads/${finiq.pdf_url}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn-pdf"
                                                        title="Descargar PDF"
                                                    >
                                                        <FaFileSignature />
                                                    </a>
                                                ) : (
                                                    <span style={{ color: '#999' }}>-</span>
                                                )}
                                            </td>
                                            <td className="actions-cell">
                                                <div className="action-buttons">
                                                    {finiq.estado === 'PENDIENTE' && (
                                                        <button
                                                            className="btn-pagar"
                                                            onClick={() => handleCambiarEstado(finiq.id, 'PAGADO')}
                                                            title="Marcar como pagado"
                                                        >
                                                            <FaCheckCircle />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn-eliminar"
                                                        onClick={() => handleEliminar(finiq.id)}
                                                        title="Eliminar finiquito"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de cálculo automático */}
            {showCalcModal && (
                <div className="modal-overlay" onClick={() => { setShowCalcModal(false); resetCalcForm(); }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Calcular Finiquito Automáticamente</h2>
                        <p className="modal-description">
                            El sistema calculará automáticamente vacaciones e indemnización según años de servicio
                        </p>
                        <form onSubmit={handleCalcular}>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Colaborador *</label>
                                    <select
                                        value={calcData.id_colaborador}
                                        onChange={(e) => setCalcData({...calcData, id_colaborador: e.target.value})}
                                        required
                                    >
                                        <option value="">Seleccione...</option>
                                        {colaboradoresDisponibles.map(col => (
                                            <option key={col.id} value={col.id}>
                                                {col.nombres} {col.apellido_paterno}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Causal de Término *</label>
                                    <select
                                        value={calcData.causal_termino}
                                        onChange={(e) => setCalcData({...calcData, causal_termino: e.target.value})}
                                        required
                                    >
                                        <option value="">Seleccione causal...</option>
                                        {CAUSALES_TERMINO.map(causal => (
                                            <option key={causal.value} value={causal.value}>
                                                {causal.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Fecha de Aviso *</label>
                                    <input
                                        type="date"
                                        value={calcData.fecha_aviso}
                                        onChange={(e) => setCalcData({...calcData, fecha_aviso: e.target.value})}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Fecha Fin de Contrato *</label>
                                    <input
                                        type="date"
                                        value={calcData.fecha_fin_contrato}
                                        onChange={(e) => setCalcData({...calcData, fecha_fin_contrato: e.target.value})}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Días de Vacaciones Tomados</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={calcData.dias_feriado_tomados}
                                        onChange={(e) => setCalcData({...calcData, dias_feriado_tomados: parseInt(e.target.value) || 0})}
                                        placeholder="0"
                                    />
                                    <small style={{color: '#666', fontSize: '11px'}}>Días ya utilizados por el trabajador</small>
                                </div>

                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={calcData.incluir_gratificacion}
                                            onChange={(e) => setCalcData({...calcData, incluir_gratificacion: e.target.checked})}
                                        />
                                        Incluir gratificación en cálculo de indemnización
                                    </label>
                                </div>
                            </div>

                            <div className="calc-info-box">
                                <FaInfoCircle style={{marginRight: '8px'}} />
                                <span>
                                    El sistema calculará automáticamente según el Código del Trabajo:
                                    feriado proporcional (15 días/año), indemnización con tope de 90 UF,
                                    y mes de aviso si corresponde.
                                </span>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowCalcModal(false); resetCalcForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-calcular">
                                    <FaCalculator /> Calcular Finiquito
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de formulario manual */}
            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Nuevo Finiquito Manual</h2>
                        <form onSubmit={handleSubmitManual}>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Colaborador *</label>
                                    <select
                                        value={formData.id_colaborador}
                                        onChange={(e) => setFormData({...formData, id_colaborador: e.target.value})}
                                        required
                                    >
                                        <option value="">Seleccione...</option>
                                        {colaboradoresDisponibles.map(col => (
                                            <option key={col.id} value={col.id}>
                                                {col.nombres} {col.apellido_paterno}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Causal de Término *</label>
                                    <select
                                        value={formData.causal_termino}
                                        onChange={(e) => setFormData({...formData, causal_termino: e.target.value})}
                                        required
                                    >
                                        <option value="">Seleccione causal...</option>
                                        {CAUSALES_TERMINO.map(causal => (
                                            <option key={causal.value} value={causal.value}>
                                                {causal.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Fecha de Aviso</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_aviso}
                                        onChange={(e) => setFormData({...formData, fecha_aviso: e.target.value})}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Fecha Fin de Contrato *</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_fin_contrato}
                                        onChange={(e) => setFormData({...formData, fecha_fin_contrato: e.target.value})}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Fecha de Pago (Finiquito) *</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_salida}
                                        onChange={(e) => setFormData({...formData, fecha_salida: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Días Vacaciones Pendientes</label>
                                    <input
                                        type="number"
                                        value={formData.dias_vacaciones_pendientes}
                                        onChange={(e) => setFormData({...formData, dias_vacaciones_pendientes: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Monto Vacaciones</label>
                                    <input
                                        type="number"
                                        value={formData.monto_vacaciones}
                                        onChange={(e) => setFormData({...formData, monto_vacaciones: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Monto Indemnización</label>
                                    <input
                                        type="number"
                                        value={formData.monto_indemnizacion}
                                        onChange={(e) => setFormData({...formData, monto_indemnizacion: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Monto Aviso Previo</label>
                                    <input
                                        type="number"
                                        value={formData.monto_aviso_previo}
                                        onChange={(e) => setFormData({...formData, monto_aviso_previo: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Otros Montos</label>
                                    <input
                                        type="number"
                                        value={formData.otros_montos}
                                        onChange={(e) => setFormData({...formData, otros_montos: e.target.value})}
                                    />
                                </div>
                                <div className="form-group full-width">
                                    <label>Observaciones</label>
                                    <textarea
                                        value={formData.observaciones}
                                        onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                                        rows="3"
                                    />
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save">
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FiniquitosPage;
